import { sendHomieIntro } from "@/lib/featureEmails.functions";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { LifeMartChromeAuto } from "@/components/lifemart/LifeMartChrome";
import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "@/lib/router-compat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Search, MapPin, Loader2, AlertCircle, LogIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLifeMart } from "@/hooks/useLifeMart";
import { Link } from "@/lib/router-compat";
import { trackEvent } from "@/lib/analytics";
import { buildStackSnapshot, saveLastStackSnapshot } from "@/lib/stackSnapshot";
import { openHomie } from "@/lib/homieBridge";
import { dgTrack } from "@/lib/dataGraph";
import { fireMetaStackRun } from "@/lib/metaPixel";
import { getLastZip, setLastZip } from "@/lib/lastZip";
import { upsertUserAttributes } from "@/lib/userAttributes";
import { isHeroStackFunnel } from "@/lib/heroCategory";
import { logLpEvent } from "@/lib/lpEvents";


import GrantStackResults, { type StackResults } from "@/components/grants/GrantStackResults";
import StackShareCard from "@/components/grants/StackShareCard";
import GoalImpactStrip from "@/components/goals/GoalImpactStrip";
import QualificationPrompt from "@/components/qualification/QualificationPrompt";

import { grantStack } from "@/lib/grantStack.functions";
import { useServerFn } from "@tanstack/react-start";

const GrantFinder = () => {
  const { user } = useAuth();
  const { isLifeMartMember } = useLifeMart();
  const runGrantStack = useServerFn(grantStack);
  const [zipCode, setZipCode] = useState("");
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<StackResults | null>(null);
  const [error, setError] = useState("");
  const [needsAuth, setNeedsAuth] = useState(false);

  /** Program names across all layers. Drives the share card, which now shows a
   *  count when the matched programs publish no dollar amounts. */
  const stackProgramNames = useMemo(
    () =>
      results
        ? (["federal", "state", "county", "employer"] as const)
            .flatMap((l) => (results.layers[l] ?? []).map((p) => p.name))
            .filter(Boolean)
        : [],
    [results],
  );

  useEffect(() => {
    const z = (searchParams.get("zip") || "").trim();
    if (/^\d{5}$/.test(z)) {
      setZipCode(z);
      setLastZip(z);
      return;
    }
    // No ZIP in the URL (refresh, email-confirmation link, direct visit): fall
    // back to the one we already know so nobody retypes it. Prefill only — the
    // auto-run below is reserved for an explicit ?zip= handoff.
    const stored = getLastZip();
    if (stored) {
      setZipCode((prev) => prev || stored);
      return;
    }
    if (!user) return;
    let cancelled = false;
    supabase
      .from("user_attributes")
      .select("zip_code")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        const pz = (data?.zip_code || "").trim();
        if (!cancelled && /^\d{5}$/.test(pz)) setZipCode((prev) => prev || pz);
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams, user]);

  // Auto-run the stack once when arriving via /stack with ?zip=XXXXX so users
  // don't have to re-submit the ZIP they already entered.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    const z = (searchParams.get("zip") || "").trim();
    if (!/^\d{5}$/.test(z)) return;
    if (zipCode !== z) return;
    autoRanRef.current = true;
    runStack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode, user, searchParams]);


  /**
   * Persist a completed Stack run automatically.
   *
   * Historically a row only landed in `grant_stack_results` when the member
   * clicked "Save to my plan" — almost nobody did, so completion looked like
   * zero and the stalled-Stack nudge emailed people who had already finished.
   * The run itself is now the signal; the Save button only awards points.
   * Deduped to one row per user + ZIP + day.
   */
  const persistRun = async (data: StackResults) => {
    if (!user) return;
    try {
      const since = new Date();
      since.setHours(0, 0, 0, 0);
      const { data: existing } = await supabase
        .from("grant_stack_results")
        .select("id")
        .eq("user_id", user.id)
        .eq("zip", data.zip)
        .gte("created_at", since.toISOString())
        .limit(1);
      if (existing && existing.length > 0) return;

      const snapshot = buildStackSnapshot({
        zip: data.zip,
        state: (data as any).state ?? null,
        employer_name: data.employer_name,
        employer_status: (data as any).employer_status ?? null,
        total_estimated: data.total_estimated,
        generated_at: (data as any).generated_at ?? null,
        layers: data.layers as any,
      });
      const { error: insertErr } = await supabase.from("grant_stack_results").insert({
        user_id: user.id,
        zip: data.zip,
        employer_name: data.employer_name,
        total_estimated: data.total_estimated,
        layers_json: data.layers as any,
        snapshot_json: snapshot as any,
      });

      // The ZIP has to follow the member, not stop at the run: lender licensing,
      // grant matching, nudges and partner briefs all read it off the profile.
      upsertUserAttributes({ zip_code: data.zip });

      // First saved Stack run → invite them to the Homie Readiness Score,
      // once ever (idempotency key is per-member). Skip if they already
      // took the quiz — nothing to introduce.
      if (!insertErr && user.email) {
        const { data: quiz } = await supabase
          .from("readiness_quiz_results")
          .select("id")
          .eq("user_id", user.id)
          .limit(1);
        if (!quiz || quiz.length === 0) {
          sendHomieIntro({
            data: {
              source: "stack",
              zip: data.zip,
              totalEstimated: data.total_estimated ?? 0,
            },
          }).catch(() => {});
        }
      }
    } catch {
      // Never block the results UI on a persistence failure.
    }
  };

  const runStack = async (overrideEmployer?: string) => {

    if (!/^\d{5}$/.test(zipCode)) {
      setError("Please enter a valid 5-digit zip code.");
      return;
    }
    if (!user) {
      setNeedsAuth(true);
      setError("");
      return;
    }
    setError("");
    setNeedsAuth(false);
    setLastZip(zipCode);
    setLoading(true);
    try {
      const data = await runGrantStack({ data: { zipCode, employerName: overrideEmployer } });
      if ((data as any)?.error) {
        setError((data as any).error);
        return;
      }
      const stackResults = data as StackResults;
      setResults(stackResults);
      const snapshot = buildStackSnapshot({
        zip: stackResults.zip,
        state: (stackResults as any).state ?? null,
        employer_name: stackResults.employer_name,
        employer_status: (stackResults as any).employer_status ?? null,
        total_estimated: stackResults.total_estimated,
        generated_at: (stackResults as any).generated_at ?? null,
        layers: stackResults.layers as any,
      });
      saveLastStackSnapshot(snapshot);
      trackEvent("grant_stack_run", { zipCode, hasEmployer: !!stackResults.employer_name });
      if (isHeroStackFunnel()) {
        trackEvent("hero_stack_auto_run_completed", {
          zip: stackResults.zip,
          program_count: snapshot.programs.length,
          total_estimated: stackResults.total_estimated,
        });
        logLpEvent("hero_stack_auto_run_completed", {
          zip: stackResults.zip,
          program_count: snapshot.programs.length,
          total_estimated: stackResults.total_estimated,
        });
      }
      dgTrack("behavioral", "grant_stack_run", {
        zip: stackResults.zip,
        total_estimated: stackResults.total_estimated,
        has_employer: !!stackResults.employer_name,
        surface: "grant_finder",
      });
      fireMetaStackRun({
        zip: stackResults.zip,
        totalEstimated: stackResults.total_estimated,
      });
      void persistRun(stackResults);


    } catch (e: any) {
      console.error(e);
      if (e?.message?.includes("401") || e?.message?.includes("Unauthorized")) {
        setNeedsAuth(true);
        return;
      }
      const msg = "Something went wrong. Please try again.";
      setError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    if (!user || !results) return;
    // The run itself already persisted the row (deduped per user + ZIP + day);
    // this is a no-op unless persistence failed at run time.
    await persistRun(results);
    trackEvent("grant_stack_saved", { zip: results.zip });

    try {
      await (supabase as any).rpc("award_phase3_points", {
        _event_key: "grant_stack_saved",
        _dedupe_key: results.zip,
        _points: isLifeMartMember ? 20 : 10,
      });
    } catch {}
    toast({
      title: isLifeMartMember ? "Saved — +20 pts (LifeMart 2×)" : "Saved to your plan",
      description: isLifeMartMember
        ? "Every program is unlocked. Homie can walk you through which to apply for first."
        : "Homie can walk you through which to apply for first.",
      action: (
        <button
          type="button"
          onClick={() =>
            openHomie({
              source: "grant-finder:saved-toast",
              prompt: `I just saved my Stack for ZIP ${results.zip}. What should I tackle first?`,
            })
          }
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
        >
          Ask Homie
        </button>
      ) as any,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Stack — federal, state, county & employer assistance in one total"
        description="See every layer of homebuyer assistance you qualify for — federal, state, county, and employer-funded — stacked into one total."
        path="/grant-finder"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Stack — Home Approach",
            url: "https://homeapproach.com/grant-finder",
            applicationCategory: "FinanceApplication",
            operatingSystem: "Any",
            browserRequirements: "Requires JavaScript",
            description: "Search and stack every layer of homebuyer assistance you qualify for — federal programs, state grants, county funds, and employer-funded down payment assistance.",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            featureList: [
              "Federal down payment assistance program search",
              "State and county grant stacking",
              "Employer-funded homebuyer assistance matching",
              "Combined total assistance calculation",
            ],
            provider: { "@type": "Organization", name: "Home Approach", url: "https://homeapproach.com" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://homeapproach.com/" },
              { "@type": "ListItem", position: 2, name: "Stack", item: "https://homeapproach.com/grant-finder" },
            ],
          },
        ]}
      />
      <Header />
      <LifeMartChromeAuto>
        <main className="container mx-auto px-4 py-10 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl sm:text-4xl text-foreground mb-2" style={{ fontFamily: "var(--heading-font)" }}>
              Stack every grant you qualify for
            </h1>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              Most platforms surface one program. We waterfall through four layers — federal, state, county, and
              your employer — so you see the full picture.
            </p>
          </motion.div>

          <div className="flex gap-3 mb-6 max-w-md">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Enter zip code (e.g. 02115)"
                autoFocus
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                onKeyDown={(e) => e.key === "Enter" && runStack()}
                className="pl-9"
                maxLength={5}
              />
            </div>
            <Button onClick={() => runStack()} disabled={loading} className="gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Stack grants
            </Button>
          </div>

          {error && !needsAuth && (
            <div className="flex items-center gap-2 text-destructive text-sm mb-6">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {needsAuth && (
            <Card className="p-6 mb-6 border-primary/20 bg-primary/5">
              <div className="flex items-start gap-3">
                <LogIn className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">
                    Your Stack for {zipCode || "your area"} checks five layers
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Federal programs, state programs, local programs, your employer benefit and the Hero Reward (if you qualify).
                    Full amounts and next steps unlock with Home Approach through your employer.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild size="sm" className="gap-2">
                      <Link to="/request-access">Unlock through my employer</Link>
                    </Button>
                    <Button asChild size="sm" variant="outline" className="gap-2">
                      <Link to={`/auth?signin=true&returnTo=${encodeURIComponent("/grant-finder")}`}>
                        <LogIn className="h-4 w-4" /> Member sign in
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Stacking programs for {zipCode}…</p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {results && !loading && (
              <motion.div key="results" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <GrantStackResults

                  data={results}
                  onSave={handleSavePlan}
                  onEmployerAdded={(name) => runStack(name)}
                  qualificationPrompt={user ? <QualificationPrompt variant="inline" /> : undefined}
                  lifemart={isLifeMartMember}
                />
                {stackProgramNames.length > 0 && (
                  <div className="mt-6">
                    <StackShareCard
                      totalEstimated={results.total_estimated}
                      programCount={stackProgramNames.length}
                      zip={results.zip}
                      state={results.state}
                      lifemart={isLifeMartMember}
                      programNames={stackProgramNames}
                    />
                  </div>
                )}

                {results.total_estimated > 0 && (
                  <GoalImpactStrip
                    goalType="down_payment"
                    proposedCurrentValue={results.total_estimated}
                    proposedTargetValue={results.total_estimated}
                    source="grant-stack"
                    helper={`Stacked up to ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(results.total_estimated)} in potential grant assistance for ${results.zip}. Counts toward your down-payment goal.`}
                    meta={{ grant_zip: results.zip, grant_state: results.state }}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </LifeMartChromeAuto>
      <Footer />
    </div>
  );
};

export default GrantFinder;
